import {
	faArrowLeft,
	faFingerprint,
	faMobileScreen,
	faSpinner,
	faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	useBiometricLogin,
	usePhoneInput,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';
import { RegistrationForm } from './RegistrationForm';
import { useTenant } from './TenantGate';

type LoginView = 'credentials' | 'phone-input' | 'code-verify' | 'register';

interface LoginModalProps {
	/** Whether the modal is visible */
	isOpen: boolean;
	/** Called when the modal should close (user dismisses or login succeeds) */
	onClose: () => void;
	/** Optional message shown above the form (e.g. "Your session has expired") */
	message?: string | null;
}

/**
 * Login modal that overlays the portal.
 *
 * Unlike a full-screen login page, this renders on top of the existing
 * portal layout so the user never loses their browsing context. Used for:
 * - Clicking "Sign In" in the header
 * - Attempting an auth-required action while not logged in
 * - Session expiry (token refresh failure)
 *
 * Supports two auth flows:
 * 1. Email/password (default)
 * 2. SMS verification code (when phoneLoginEnabled is true for the tenant)
 */
/**
 * Mask a phone number for display, showing only the last 4 digits.
 * e.g. "+15551234567" → "(•••) •••-4567"
 */
function maskPhoneNumber(phone: string): string {
	const digits = phone.replace(/\D/g, '');
	if (digits.length < 4) return '•'.repeat(digits.length);
	const last4 = digits.slice(-4);
	return `(•••) •••-${last4}`;
}

export function LoginModal({
	isOpen,
	onClose,
	message,
}: LoginModalProps): JSX.Element | null {
	const { t } = useTranslation();
	const { name: tenantName, authOptions } = useTenant();
	const brandLogoUrl = usePortalSiteStore((s) => s.site?.logoUrl ?? null);
	const loginBackgroundUrl = usePortalSiteStore(
		(s) => s.site?.loginBackgroundUrl ?? null,
	);
	// Self-serve registration is per-portal-site (off by default). The
	// "Register" link only renders when the active brand has opted in via
	// the admin editor; the server re-checks anyway, so flipping this
	// client-side does not bypass anything.
	const isRegistrationEnabled = usePortalSiteStore(
		(s) => s.site?.isRegistrationEnabled ?? false,
	);
	const tenantAlias = usePortalSiteStore((s) => s.site?.brandAlias ?? null);
	const {
		login,
		requestSmsCode,
		loginWithSms,
		isLoading,
		isAuthenticated,
		needsClientSelection,
		selectClient,
		availableClients,
	} = usePortalAuth();

	// View state machine: credentials → phone-input → code-verify
	const [view, setView] = useState<LoginView>('credentials');

	// Email/password form state
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');

	// SMS login state
	const [phoneNumber, setPhoneNumber] = useState('');
	const phoneInput = usePhoneInput(phoneNumber, setPhoneNumber);
	const [smsDigits, setSmsDigits] = useState<Array<string>>([
		'',
		'',
		'',
		'',
		'',
		'',
	]);
	const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
	const [resendDisabledUntil, setResendDisabledUntil] = useState<number | null>(
		null,
	);
	const [isSendingCode, setIsSendingCode] = useState(false);
	// Single tick counter drives all countdown displays — avoids multiple
	// setState calls per interval which caused cascading re-render warnings
	const [, setTick] = useState(0);

	// Shared state
	const [error, setError] = useState<string | null>(null);
	const [showClientPicker, setShowClientPicker] = useState(false);
	const [selectingClientId, setSelectingClientId] = useState<number | null>(
		null,
	);

	// Biometric (Face ID / Touch ID / fingerprint) support. Only renders
	// any UI when running inside a native Capacitor shell AND the device
	// has a usable biometric enrolled — `isNative` and `isAvailable` both
	// resolve false on the web build, so the storefront never shows
	// biometric chrome to desktop visitors.
	const biometric = useBiometricLogin();
	const showBiometricUi = biometric.isNative && biometric.isAvailable;
	const refreshToken = usePortalAuthStore((s) => s.refreshToken);
	const [enableBiometricNext, setEnableBiometricNext] = useState(false);
	const [isBiometricLoading, setIsBiometricLoading] = useState(false);

	// One-shot biometric quick-login: when the user has previously enabled
	// it on this device, hitting the modal triggers the OS prompt and, on
	// success, hands the stored refresh token to the existing OAuth refresh
	// path. On cancel/failure we silently fall through to the form.
	const handleBiometricLogin = useCallback(async () => {
		if (!showBiometricUi) return;
		setIsBiometricLoading(true);
		setError(null);
		try {
			const session = await biometric.tryAutoLogin(`Sign in to ${tenantName}`);
			if (session != null) {
				// Drop the token into the auth store and let the existing
				// usePortalAuth refresh effect pick it up. We don't call
				// `login()` because we don't have the password — the
				// stored refresh token IS the credential here.
				usePortalAuthStore.getState().setRefreshToken(session.refreshToken);
				usePortalAuthStore.getState().persist();
			}
		} catch (e) {
			console.debug('[biometric] auto-login error', e);
		} finally {
			setIsBiometricLoading(false);
		}
	}, [biometric, showBiometricUi, tenantName]);

	// After a successful credential login, if the user opted in and we
	// have a fresh refreshToken in the store, save it to the OS keychain
	// behind biometric protection. Runs once per successful login.
	useEffect(() => {
		if (
			!enableBiometricNext ||
			!showBiometricUi ||
			refreshToken == null ||
			email.trim().length === 0
		) {
			return;
		}
		void biometric.enable({ email: email.trim(), refreshToken });
	}, [enableBiometricNext, showBiometricUi, refreshToken, email, biometric]);

	const emailRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);

	// Auto-focus the appropriate input when modal opens or view changes
	useEffect(() => {
		if (!isOpen) {
			// Reset all form state when modal closes
			setView('credentials');
			setEmail('');
			setPassword('');
			setPhoneNumber('');
			setSmsDigits(['', '', '', '', '', '']);
			setSmsRawInput('');
			setCodeExpiresAt(null);
			setResendDisabledUntil(null);
			setIsSendingCode(false);
			setError(null);
			setShowClientPicker(false);
			setSelectingClientId(null);
			return;
		}

		const timer = setTimeout(() => {
			if (view === 'credentials') {
				emailRef.current?.focus();
			} else if (view === 'phone-input') {
				phoneRef.current?.focus();
			} else if (view === 'code-verify') {
				hiddenInputRef.current?.focus();
			}
		}, 100);

		return () => clearTimeout(timer);
	}, [isOpen, view]);

	// If login succeeded and no client selection needed, close the modal
	useEffect(() => {
		if (isOpen && isAuthenticated && !needsClientSelection) {
			onClose();
		}
	}, [isOpen, isAuthenticated, needsClientSelection, onClose]);

	// After login, if client selection is needed, show the picker
	useEffect(() => {
		if (isOpen && needsClientSelection && availableClients.length > 1) {
			setShowClientPicker(true);
		}
	}, [isOpen, needsClientSelection, availableClients]);

	// Tick every second to drive countdown displays. Only one setState per tick
	// instead of multiple (countdownText + resendCountdown), which was causing
	// cascading re-render warnings between LoginModal and UserMenu.
	useEffect(() => {
		if (view !== 'code-verify') return;

		const interval = setInterval(() => {
			setTick((prev) => prev + 1);

			// Clear resend cooldown when expired
			if (resendDisabledUntil != null && Date.now() >= resendDisabledUntil) {
				setResendDisabledUntil(null);
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [view, resendDisabledUntil]);

	// Derive countdown displays from timestamps on each render (driven by tick)
	const now = Date.now();
	const codeExpiryRemaining =
		codeExpiresAt != null
			? Math.max(0, Math.ceil((codeExpiresAt - now) / 1000))
			: 0;
	const codeExpiryText =
		codeExpiryRemaining > 0
			? `${Math.floor(codeExpiryRemaining / 60)}:${(codeExpiryRemaining % 60).toString().padStart(2, '0')}`
			: '';
	const resendRemaining =
		resendDisabledUntil != null
			? Math.max(0, Math.ceil((resendDisabledUntil - now) / 1000))
			: 0;

	// --- Email/password login ---
	const handleCredentialsSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setError(null);

			if (email.trim().length === 0 || password.length === 0) {
				setError(t('Please enter your email and password.'));
				return;
			}

			try {
				await login(email.trim(), password);
			} catch (err: unknown) {
				const msg = err instanceof Error ? err.message : t('Login failed');
				setError(t(msg));
			}
		},
		[email, password, login, t],
	);

	// --- SMS login: request code ---
	const handleRequestCode = useCallback(
		async (e?: React.FormEvent) => {
			e?.preventDefault();
			setError(null);

			if (phoneNumber.trim().length === 0) {
				setError(t('Please enter your phone number.'));
				return;
			}

			setIsSendingCode(true);
			try {
				const result = await requestSmsCode(phoneNumber.trim());
				setCodeExpiresAt(Date.now() + result.expiresIn * 1000);
				setResendDisabledUntil(Date.now() + 60_000); // 60s cooldown
				setSmsDigits(['', '', '', '', '', '']);
				setSmsRawInput('');
				setView('code-verify');
			} catch (err: unknown) {
				const msg =
					err instanceof Error ? err.message : t('Failed to send code');
				setError(t(msg));
			} finally {
				setIsSendingCode(false);
			}
		},
		[phoneNumber, requestSmsCode, t],
	);

	// --- SMS login: resend code ---
	const handleResendCode = useCallback(async () => {
		setError(null);
		setIsSendingCode(true);
		try {
			const result = await requestSmsCode(phoneNumber.trim());
			setCodeExpiresAt(Date.now() + result.expiresIn * 1000);
			setResendDisabledUntil(Date.now() + 60_000);
			setSmsDigits(['', '', '', '', '', '']);
			setSmsRawInput('');
			hiddenInputRef.current?.focus();
		} catch (err: unknown) {
			const msg =
				err instanceof Error ? err.message : t('Failed to resend code');
			setError(t(msg));
		} finally {
			setIsSendingCode(false);
		}
	}, [phoneNumber, requestSmsCode, t]);

	// --- SMS login: verify code ---
	const handleVerifyCode = useCallback(
		async (digits: Array<string>) => {
			const code = digits.join('');
			if (code.length !== 6) return;

			setError(null);
			try {
				await loginWithSms(phoneNumber.trim(), code);
			} catch (err: unknown) {
				const msg = err instanceof Error ? err.message : t('Invalid code');
				setError(t(msg));
				// Clear digits and refocus first input on failure
				setSmsDigits(['', '', '', '', '', '']);
				setSmsRawInput('');
				hiddenInputRef.current?.focus();
			}
		},
		[phoneNumber, loginWithSms, t],
	);

	// --- Single hidden input approach for 6-digit code ---
	// Uses one real <input> for keyboard/autofill/paste, with visual-only
	// digit boxes. This is the standard pattern (Stripe, Auth0, etc.) that
	// avoids all the focus/autofill/backspace issues of 6 separate inputs.
	const [smsRawInput, setSmsRawInput] = useState('');
	const [isCodeFocused, setIsCodeFocused] = useState(false);
	const hiddenInputRef = useRef<HTMLInputElement>(null);

	const handleCodeInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
			setSmsRawInput(digits);

			// Update the visual digit boxes
			const digitArray = digits.split('');
			setSmsDigits(Array.from({ length: 6 }, (_, i) => digitArray[i] ?? ''));

			// Auto-submit when all 6 digits are entered
			if (digits.length === 6) {
				handleVerifyCode(digits.split(''));
			}
		},
		[handleVerifyCode],
	);

	// --- Client picker ---
	const handleSelectClient = useCallback(
		async (clientId: number) => {
			setSelectingClientId(clientId);
			try {
				await selectClient(clientId);
			} catch (err: unknown) {
				const msg =
					err instanceof Error ? err.message : t('Failed to select account');
				setError(t(msg));
				setSelectingClientId(null);
			}
		},
		[selectClient, t],
	);

	if (!isOpen) return null;

	// --- Determine subtitle based on current view ---
	const getSubtitle = (): string => {
		if (showClientPicker) return t('Select your account');
		if (view === 'phone-input') return t('Enter your phone number');
		if (view === 'code-verify') return t('Enter verification code');
		if (view === 'register') return t('Request a customer account');
		return t('Sign in to your account');
	};

	return (
		// Mobile (default): top-anchored so the email/password fields stay
		// visible when the iOS soft keyboard pops up — keyboards eat
		// ~290–336pt at the bottom and a vertically-centered modal would
		// slide its inputs straight under the keyboard.
		// Desktop (md+): center the modal as expected for a sign-in
		// dialog overlaying the page.
		<div
			className="fixed inset-0 z-50 flex items-start md:items-center justify-center px-4 pb-4 overflow-y-auto bg-brand-login-bg"
			style={{
				paddingTop: 'max(env(safe-area-inset-top), 1.5rem)',
			}}
		>
			{/*
			  Backdrop. When the active portal_site has a `loginBackgroundUrl`,
			  the brand image fills the backdrop with a dark gradient on top
			  for legibility — turning the LoginModal into a branded
			  experience rather than a generic dark scrim. Falls back to the
			  plain dark scrim for tenants without a configured background.

			  The image is capped at one viewport-height and pinned to the
			  top of the wrapper. When the modal panel grows taller than
			  the viewport (the registration view in particular, with all
			  its address/business fields), the wrapper's `bg-brand-login-bg`
			  (= configured loginBackgroundColor, falling back to
			  primaryColor) fills the area below the image instead of
			  letting the catalog peek through — which was the bug on iOS
			  WebView, where `absolute inset-0 h-full w-full object-cover`
			  failed to extend the image past the scrollable content.

			  Not dismissible: the user must use the close button or
			  complete login.
			*/}
			{loginBackgroundUrl != null && loginBackgroundUrl !== '' ? (
				<div
					aria-hidden="true"
					className="absolute inset-x-0 top-0 h-dvh overflow-hidden pointer-events-none"
				>
					<img
						src={loginBackgroundUrl}
						alt=""
						className="absolute inset-0 h-full w-full object-cover"
					/>
					<div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/55 to-black/70 backdrop-blur-[2px]" />
				</div>
			) : (
				<div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
			)}

			{/* Modal */}
			<div
				className={`relative w-full mx-4 bg-black/40 backdrop-blur-2xl text-white rounded-2xl shadow-2xl shadow-black/50 ring-1 ring-white/15 overflow-hidden ${
					view === 'register' && !showClientPicker
						? 'max-w-lg'
						: view === 'code-verify' && !showClientPicker
							? 'max-w-md'
							: 'max-w-sm'
				}`}
			>
				{/* Close button */}
				<button
					type="button"
					onClick={onClose}
					className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors z-10"
					aria-label={t('Close')}
				>
					<FontAwesomeIcon icon={faTimes} className="text-sm" />
				</button>

				<div className="p-6">
					{/* Brand */}
					<div className="text-center mb-6">
						{brandLogoUrl != null && brandLogoUrl !== '' ? (
							// Logo is the brand identity — don't double-print
							// the tenant name as text underneath it.
							<div className="mb-2 flex justify-center">
								<img
									src={brandLogoUrl}
									alt={t(tenantName)}
									className="h-14 object-contain"
								/>
							</div>
						) : (
							<h2 className="text-xl font-bold text-white">{t(tenantName)}</h2>
						)}
						<p className="mt-1 text-sm text-white/70">{getSubtitle()}</p>
					</div>

					{/* Session expired or custom message */}
					{message != null && (
						<div className="mb-4 bg-amber-500/15 border border-amber-400/30 text-amber-200 text-sm rounded-lg px-4 py-3">
							{t(message)}
						</div>
					)}

					{/* Error */}
					{error != null && (
						<div className="mb-4 bg-red-500/15 border border-red-400/30 text-red-200 text-sm rounded-lg px-4 py-3">
							{error}
						</div>
					)}

					{/* Client picker (shown after login if multi-client) */}
					{showClientPicker ? (
						<div className="space-y-2">
							{availableClients.map((client) => (
								<button
									key={client.id}
									type="button"
									onClick={() => handleSelectClient(client.id)}
									disabled={isLoading}
									className="w-full flex items-center gap-3 px-4 py-3 bg-white/10 border border-white/15 rounded-lg hover:border-portal-accent transition-all min-h-touch disabled:opacity-50 text-left"
								>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium text-white truncate">
											{t(client.name)}
										</div>
										{client.externalId != null && (
											<div className="text-xs text-white/60">
												{t(client.externalId)}
											</div>
										)}
									</div>
									{selectingClientId === client.id && (
										<FontAwesomeIcon
											icon={faSpinner}
											className="animate-spin text-portal-accent shrink-0"
										/>
									)}
								</button>
							))}
						</div>
					) : view === 'credentials' ? (
						/* === Email/password login form === */
						<form onSubmit={handleCredentialsSubmit} className="space-y-4">
							{/* Biometric quick-login — native shells only AND only
							    when the device has an enrolled biometric AND the
							    user has previously enabled it on this app. Web
							    builds of the storefront never see this. */}
							{showBiometricUi && biometric.hasStoredSession && (
								<button
									type="button"
									onClick={handleBiometricLogin}
									disabled={isBiometricLoading || isLoading}
									className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent/15 hover:bg-portal-accent/25 disabled:opacity-60 text-white border border-portal-accent/40 font-medium rounded-lg transition-colors min-h-touch"
								>
									{isBiometricLoading ? (
										<FontAwesomeIcon
											icon={faSpinner}
											className="animate-spin"
										/>
									) : (
										<FontAwesomeIcon icon={faFingerprint} />
									)}
									{isBiometricLoading
										? t('Authenticating...')
										: t(`Sign in with ${biometric.biometryName}`)}
								</button>
							)}

							<div>
								<label
									htmlFor="login-email"
									className="block text-sm font-medium text-white/85 mb-1.5"
								>
									{t('Email')}
								</label>
								<input
									ref={emailRef}
									id="login-email"
									type="email"
									autoComplete="email"
									autoCapitalize="none"
									autoCorrect="off"
									spellCheck={false}
									inputMode="email"
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder={t('you@example.com')}
									className="w-full px-3 py-3 rounded-lg bg-white border border-white/30 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm"
									disabled={isLoading}
								/>
							</div>

							<div>
								<label
									htmlFor="login-password"
									className="block text-sm font-medium text-white/85 mb-1.5"
								>
									{t('Password')}
								</label>
								<input
									id="login-password"
									type="password"
									autoComplete="current-password"
									autoCapitalize="none"
									autoCorrect="off"
									spellCheck={false}
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder={t('Enter your password')}
									className="w-full px-3 py-3 rounded-lg bg-white border border-white/30 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm"
									disabled={isLoading}
								/>
							</div>

							{/* Biometric opt-in for next time — native shells only AND
							    only when no session is currently stored. Hides on
							    web automatically because showBiometricUi is false. */}
							{showBiometricUi && !biometric.hasStoredSession && (
								<label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer select-none">
									<input
										type="checkbox"
										checked={enableBiometricNext}
										onChange={(e) => setEnableBiometricNext(e.target.checked)}
										className="h-4 w-4 rounded border-white/30 bg-white/10 text-portal-accent focus:ring-2 focus:ring-portal-accent focus:ring-offset-0"
										disabled={isLoading}
									/>
									<span>
										{t(`Enable ${biometric.biometryName} for next sign-in`)}
									</span>
								</label>
							)}

							<button
								type="submit"
								disabled={isLoading}
								className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white font-medium rounded-lg transition-colors min-h-touch"
							>
								{isLoading && (
									<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
								)}
								{isLoading ? t('Signing in...') : t('Sign In')}
							</button>

							{/* Forgot-password link. Closes the modal first so the
							    user lands on /forgot-password without an overlay
							    floating on top. */}
							<div className="text-center">
								<a
									href="/forgot-password"
									onClick={(e) => {
										e.preventDefault();
										onClose();
										window.location.href = '/forgot-password';
									}}
									className="text-sm text-portal-accent hover:underline"
								>
									{t('Forgot your password?')}
								</a>
							</div>

							{/* Phone login option — only shown when enabled for this tenant */}
							{authOptions.phoneLoginEnabled && (
								<div className="pt-2 border-t border-white/10">
									<button
										type="button"
										onClick={() => {
											setError(null);
											setView('phone-input');
										}}
										className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:text-portal-accent transition-colors"
									>
										<FontAwesomeIcon icon={faMobileScreen} />
										{t('Sign in with phone')}
									</button>
								</div>
							)}

							{/* Register link — only visible when the active portal_site
							    has opted into self-serve registration. Click switches
							    the modal into the registration view. */}
							{isRegistrationEnabled && (
								<p className="text-center text-xs text-white/60 mt-3">
									{t("Don't have an account?")}{' '}
									<button
										type="button"
										className="text-portal-accent hover:underline"
										onClick={() => {
											setError(null);
											setView('register');
										}}
									>
										{t('Register')}
									</button>
								</p>
							)}
						</form>
					) : view === 'register' ? (
						/* === Self-serve registration form === */
						tenantAlias != null ? (
							<RegistrationForm
								tenantAlias={tenantAlias}
								variant="modal"
								onCancel={() => {
									setError(null);
									setView('credentials');
								}}
								onComplete={() => {
									setError(null);
									setView('credentials');
								}}
							/>
						) : (
							<div className="text-sm text-white/70 text-center py-6">
								{t('This storefront is still loading. Try again in a moment.')}
							</div>
						)
					) : view === 'phone-input' ? (
						/* === Phone number input === */
						<form onSubmit={handleRequestCode} className="space-y-4">
							<div>
								<label
									htmlFor="login-phone"
									className="block text-sm font-medium text-white/85 mb-1.5"
								>
									{t('Phone Number')}
								</label>
								<input
									ref={phoneRef}
									id="login-phone"
									type="tel"
									autoComplete="tel"
									inputMode="tel"
									required
									value={phoneInput.value}
									onChange={phoneInput.onChange}
									placeholder={t('(555) 123-4567')}
									className="w-full px-3 py-3 rounded-lg bg-white border border-white/30 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-portal-accent focus:border-transparent shadow-sm"
									disabled={isLoading}
								/>
							</div>

							<button
								type="submit"
								disabled={isSendingCode}
								className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:opacity-60 text-white font-medium rounded-lg transition-colors min-h-touch"
							>
								{isSendingCode && (
									<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
								)}
								{isSendingCode ? t('Sending code...') : t('Send Code')}
							</button>

							<button
								type="button"
								onClick={() => {
									setError(null);
									setView('credentials');
								}}
								className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
							>
								<FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
								{t('Back to email login')}
							</button>
						</form>
					) : (
						/* === Code verification === */
						<div className="space-y-4">
							<p className="text-sm text-white/70 text-center">
								{t('We sent a 6-digit code to')}{' '}
								<span className="font-medium text-white">
									{maskPhoneNumber(phoneNumber)}
								</span>
							</p>

							{/* 6-digit code input — single hidden input with visual digit boxes.
							    This is the standard OTP pattern (Stripe, Auth0) that handles
							    autofill, paste, and backspace naturally without focus juggling. */}
							<div className="relative">
								{/* Hidden input captures all keyboard/autofill/paste events */}
								<input
									ref={hiddenInputRef}
									type="text"
									inputMode="numeric"
									autoComplete="one-time-code"
									data-lpignore="true"
									data-1p-ignore="true"
									data-bwignore="true"
									data-form-type="other"
									pattern="[0-9]*"
									maxLength={6}
									value={smsRawInput}
									onChange={handleCodeInputChange}
									disabled={isLoading}
									onFocus={() => setIsCodeFocused(true)}
									onBlur={() => setIsCodeFocused(false)}
									className="absolute inset-0 w-full h-full cursor-pointer"
									style={{ opacity: 0 }}
									aria-label={t('Verification code')}
								/>
								{/* Visual digit boxes — clicking anywhere focuses the hidden input */}
								<div
									className="flex justify-center gap-3"
									onClick={() => hiddenInputRef.current?.focus()}
								>
									{smsDigits.map((digit, i) => {
										const isCursor = isCodeFocused && smsRawInput.length === i;
										const isFilled = digit !== '';
										const isFocusedGroup = isCodeFocused;

										return (
											<div
												key={i}
												className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold rounded-lg border-2 transition-all text-white ${
													isCursor
														? 'border-portal-accent bg-white/10 shadow-md shadow-portal-accent/20'
														: isFocusedGroup && !isFilled
															? 'border-portal-accent/60 bg-white/10'
															: isFilled
																? 'border-white/25 bg-white/10'
																: 'border-white/15 bg-white/5'
												}`}
											>
												{isFilled ? (
													digit
												) : isCursor ? (
													<div className="w-0.5 h-6 bg-portal-accent animate-pulse rounded-full" />
												) : null}
											</div>
										);
									})}
								</div>
							</div>
							{!isCodeFocused && smsRawInput.length === 0 && !isLoading && (
								<p
									className="text-xs text-white/50 text-center mt-2 cursor-pointer"
									onClick={() => hiddenInputRef.current?.focus()}
								>
									{t('Tap above to enter code')}
								</p>
							)}

							{/* Loading indicator when verifying */}
							{isLoading && (
								<div className="flex items-center justify-center gap-2 text-sm text-portal-accent">
									<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
									{t('Verifying...')}
								</div>
							)}

							{/* Countdown + resend */}
							<div className="text-center text-sm text-white/60">
								{codeExpiryText !== '' && (
									<p>
										{t('Code expires in')} {codeExpiryText}
									</p>
								)}
								<button
									type="button"
									onClick={handleResendCode}
									disabled={isLoading || isSendingCode || resendRemaining > 0}
									className="mt-2 text-portal-accent hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
								>
									{resendRemaining > 0
										? `${t('Resend code in')} ${resendRemaining}s`
										: t('Resend code')}
								</button>
							</div>

							{/* Back to phone input */}
							<button
								type="button"
								onClick={() => {
									setError(null);
									setSmsDigits(['', '', '', '', '', '']);
									setSmsRawInput('');
									setView('phone-input');
								}}
								className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
							>
								<FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
								{t('Change phone number')}
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
